import React, { useRef } from 'react';
import { Button, Space, Typography, Divider } from 'antd';
import { QuoteLineItemProps } from '@partsbadger/types';
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    DownOutlined,
    EditOutlined,
    UploadOutlined,
    UpOutlined,
} from '@ant-design/icons';
import UIStore from '@/stores/UIStore';

const { Text } = Typography;

interface Step {
    id: number;
    name: string;
    component: string;
}

interface StepsNavigatorProps {
    isExpanded: boolean;
    steps: Step[];
    part: QuoteLineItemProps;
    onStepClick?: (partId: QuoteLineItemProps['id'], stepId: number) => void;
    currentStep: number;
    stepRefs?: React.MutableRefObject<Record<number, HTMLButtonElement | null>>;
    /** When true (e.g. viewport ≤768px), show steps in one column with edit icon instead of dropdown */
    singleColumn?: boolean;
}

export const StepsNavigator: React.FC<StepsNavigatorProps> = ({
    steps,
    part,
    onStepClick,
    currentStep,
    stepRefs,
    singleColumn = false,
    ...props
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Map component types to part values
    const selectedMaterial = part?.product?.material_related;

    const shouldShowMaterial =
        !!selectedMaterial?.id &&
        Array.isArray(selectedMaterial.manufacturing_types) &&
        selectedMaterial.manufacturing_types.some(
            type => type.toLowerCase() === (part.manufacturing_type || '').toLowerCase()
        );

    const materialDisplayValue = shouldShowMaterial
        ? selectedMaterial.name === 'Custom'
            ? `Custom ${part.product?.custom_material? ": "+ part.product?.custom_material : ''}`
            : selectedMaterial.name || ''
        : '';

    const valueByComponent: Record<string, string> = {
        process: part.manufacturing_type || '',
        material: materialDisplayValue, 
        surface:
            part.finish?.name === 'Custom' ? `Custom: ${part.product?.custom_finish || ''}` : part.finish?.name || '',
        coating:
            part.coating?.name === 'Custom'
                ? `Custom: ${part.product?.custom_coating || ''}`
                : part.coating?.name || '',
    };

    const activeColor = '#000';

    const handleStepClick = (e: React.MouseEvent, stepId: number) => {
        e.stopPropagation();
        onStepClick?.(part.id, stepId);
    };

    if (singleColumn) {
        return (
            <div className="step-navigator w-full">
                <div className="flex flex-col gap-2">
                    {steps.map(step => {
                        const selectedValue = valueByComponent[step.component] || '';
                        const isCurrentStep = currentStep === step.id && props.isExpanded;
                        return (
                            <div
                                key={step.id}
                                role="button"
                                tabIndex={0}
                                data-testid={`step-btn-${step.component}`}
                                aria-expanded={isCurrentStep}
                                className="flex items-center justify-between gap-2 w-full py-2 px-3 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 cursor-pointer min-w-0"
                                style={{
                                    borderColor: isCurrentStep ? activeColor : undefined,
                                    backgroundColor: isCurrentStep ? 'rgba(0,0,0,0.04)' : undefined,
                                }}
                                onClick={e => handleStepClick(e, step.id)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleStepClick(e as unknown as React.MouseEvent, step.id);
                                    }
                                }}
                            >
                                <div className="flex flex-col items-start min-w-0 flex-1">
                                    <span className="text-medium font-normal text-slate-600 text-xs">{step.name}</span>
                                    <span className="text-sm font-medium text-slate-900 truncate w-full" title={selectedValue || step.name}>
                                        {selectedValue || 'Select a value'}
                                    </span>
                                </div>
                                <EditOutlined className="flex-shrink-0 text-slate-500" style={{ fontSize: 16 }} />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div ref={scrollContainerRef} className="step-navigator w-full overflow-x-auto">
            <div className="flex flex-nowrap items-start lg:items-center gap-2 min-w-max lg:min-w-0">
                {steps.map((step, index) => {
                    const selectedValue = valueByComponent[step.component] || '';
                    const hasSelection = selectedValue !== '';
                    const isCurrentStep = currentStep === step.id && props.isExpanded;

                    return (
                        <React.Fragment key={step.id}>
                            <div className="flex items-center flex-none md:flex-1 min-w-[160px] lg:min-w-0">
                                <div className="flex flex-col items-start flex-1 min-w-0">
                                    <div className={'text-medium font-normal text-slate-900'}>{step.name}:</div>

                                    <Button
                                        size="middle"
                                        className="
                                        h-10
                                        w-full
                                        max-w-[260px]
                                    "
                                        style={{
                                            height: 40,
                                            backgroundColor: isCurrentStep ? activeColor : 'white',
                                            border: isCurrentStep ? activeColor : '1px solid #d4d4d4',
                                            color: isCurrentStep ? '#fff' : undefined,
                                        }}
                                        type={'ghost'}
                                        data-testid={`step-btn-${step.component}`}
                                        aria-expanded={isCurrentStep}
                                        onClick={e => handleStepClick(e, step.id)}
                                        ref={el => {
                                            if (stepRefs && el instanceof HTMLButtonElement) {
                                                stepRefs.current[step.id] = el;
                                            }
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {selectedValue ? (
                                                <span className="text-xs font-bold truncate" title={step.name}>
                                                    {selectedValue}
                                                </span>
                                            ) : (
                                                <span className="text-xs  truncate" title={step.name}>
                                                    {'Select a value'}
                                                </span>
                                            )}

                                            <span
                                                style={{
                                                    marginLeft: 'auto',
                                                    color: isCurrentStep ? '#fff' : 'rgb(160, 160, 160)',
                                                }}
                                            >
                                                <DownOutlined />
                                            </span>
                                        </span>
                                    </Button>

                                    <div
                                        className={`mt-1 text-md f w-full truncate cursor-pointer ${
                                            hasSelection ? 'text-slate' : 'text-slate'
                                        }`}
                                        style={{
                                            fontWeight: 500,
                                        }}
                                        title={selectedValue || '-'}
                                        onClick={e => handleStepClick(e, step.id)}
                                    >
                                        {/*{selectedValue || '-'} <EditOutlined/>*/}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};
